import { PrototypeMapPrices } from '../../utils/prototypeMapPrices.js';
import Alpine from 'alpinejs';
import { normalizeSearchBedrooms } from '../../utils/searchBedrooms.js';
import { normalize } from '../../utils/helpers.js';
import {
    availabilityScarcityDismissed,
    availabilityScarcityImpression,
    searchInitiated,
    viewItemList
} from '../../utils/dataLayer.js';
import { parseLocationSearchState } from '../../utils/searchUrlState.js';
import { appendSearchAvailabilityParams } from '../../utils/searchAvailabilityParams.js';
import { pollRentalCollectionAvailability } from '../../utils/rentalCollectionAvailabilityState.js';
import { readJsonResponse } from '../../utils/authResponse.js';
import {
    assertJsonResponse,
    HttpResponseError
} from '../../utils/httpResponse.js';
import {
    shouldResetUrgencyDismissal,
    shouldTrackUrgencyImpression
} from '../../utils/searchUrgencyState.js';

const prototypeMapPrices = new PrototypeMapPrices();

const RECENT_SEARCHES_KEY = 'avari_recent_searches_v1';
const RECENT_SEARCH_LIMIT = 3;
const RECENT_SEARCH_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const FLEXIBLE_STAY_OPTIONS = Object.freeze([
    Object.freeze({
        value: '2-3',
        label: '2–3 nights',
        minimumNights: 2,
        maximumNights: 3
    }),
    Object.freeze({
        value: '4-5',
        label: '4–5 nights',
        minimumNights: 4,
        maximumNights: 5
    }),
    Object.freeze({
        value: '7',
        label: '1 week',
        minimumNights: 7,
        maximumNights: 7
    }),
    Object.freeze({
        value: '14',
        label: '2 weeks',
        minimumNights: 14,
        maximumNights: 14
    }),
    Object.freeze({
        value: '30',
        label: '1 month',
        minimumNights: 30,
        maximumNights: 30
    })
]);

Alpine.store('search', {
    emptyUrgency(reason = 'not_loaded') {
        return {
            enabled: false,
            level: null,
            message: null,
            eligibleCount: 0,
            availableCount: 0,
            percentage: null,
            reason
        };
    },

    // Core search params
    where: '',
    whereData: null, // { lat: number|null, lng: number|null, slug?: string, placeType?: string, name?: string }
    composerDestinationSlug: null,
    searchPath: '/search',
    endpoint: '/guesty/search/index',
    mapAreaLabel: null, // 'Homes in map area' or 'Search vacation homes in X'
    virtualScope: false, // Internal preset scope, e.g. rentalCollection landing page results.
    collectionMinNights: 0,
    _visualWhereMatch: null, // When set, overlay shows only when where === this value
    visualLabel: null, // Computed by Alpine.effect in init()
    activeFilterCountMode: 'groups',
    filterCountGroups: [],
    executedWhere: '', // Snapshot of 'where' at the time of search execution
    dates: '',
    dateMode: 'exact',
    flexibleMonth: '',
    flexibleNights: 2,
    flexibleMaxNights: 3,
    sort: 'relevance', // relevance | price_low | price_high | rating_high
    guests: 1,
    adults: 1,
    children: 0,
    pets: 0,
    recentSearches: [],

    // Visibility State (Manually managed for stability)
    showResults: true,
    showSkeleton: false,

    // Filter state
    filters: {
        propertyType: [],
        priceMin: 0,
        priceMax: 10000,
        bedrooms: 1,
        beds: 1,
        bathrooms: 1,
        amenities: [],
        accessibilityFeatures: []
    },
    normalizeBedrooms(value) {
        return normalizeSearchBedrooms(value);
    },
    // Core params

    // Map bounds state (for search-as-you-move)
    bounds: null, // { north, south, east, west }
    boundsSearchEnabled: false, // true when user has moved map
    currentAbortController: null, // For cancelling in-flight requests
    lastIssuedRequestId: 0, // Track which request was last issued (for "latest wins")
    _lastSearchSource: 'user', // Track source of last search ('bounds' | 'filters' | 'destination' | 'user')
    hoveredListingId: null, // ID of listing currently hovered on map
    cardHoveredListingId: null, // ID of listing currently hovered on card (for marker highlighting)

    // Live filter count state (preview inside modal, does NOT touch results.filteredCount)
    _filterCountController: null,
    _debouncedFilterCount: null,
    filterCountLoading: false,
    filterCountStatus: 'idle', // idle | loading | pending | ready | unavailable
    liveFilterCount: null,
    urgencyDismissed: false,

    // Results state
    results: {
        pricingMode: null,
        pricingContext: null,
        mapPriceStates: {},
        mapPriceVersion: 0,
        listings: [],
        mapPoints: [],
        listingPrices: {},
        listingStays: {},
        pricedNights: 0,
        totalCount: 0,
        filteredCount: 0,
        urgencyDiagnostics: null,
        urgency: {
            enabled: false,
            level: null,
            message: null,
            eligibleCount: 0,
            availableCount: 0,
            percentage: null,
            reason: 'not_loaded'
        }
    },

    // Pagination state
    pagination: {
        currentPage: 1,
        limit: 12,
        offset: 0,
        totalPages: 1
    },

    // UI state
    ui: {
        isLoading: false,
        isLoadingMore: false,
        isSubmittingSearch: false,
        hasSearched: false,
        error: null,
        errorTitle: null,
        errorKind: null,
        validationMessage: null,
        backendMessage: null, // Message from backend API (e.g., "Please select a destination")
        availabilityPendingPhase: null, // null | checking | slow
        mapMode: false,
        mapModeUiToggled: true
    },

    // Price range defaults for reset
    priceDefaults: {
        min: 0,
        max: 10000
    },

    formatPriceDisplay(value) {
        return '$' + Number(value).toLocaleString();
    },

    formatCount(value) {
        return Number(value || 0).toLocaleString();
    },

    get filterCountContextKey() {
        // Only request inputs belong in this key. Count and loading state are
        // deliberately excluded so updating the UI cannot trigger the watcher.
        return JSON.stringify([
            this.where,
            this.whereData,
            this.dates,
            this.dateMode,
            this.flexibleMonth,
            this.flexibleNights,
            this.flexibleMaxNights,
            this.guests,
            this.adults,
            this.children,
            this.petCount,
            this.filters,
            this.bounds
        ]);
    },

    isVirtualRentalCollectionScope() {
        return (
            this.virtualScope &&
            this.whereData?.placeType === 'rentalCollection'
        );
    },

    get collectionStayValidationMessage() {
        if (
            this.collectionMinNights <= 1 ||
            this.dateMode !== 'exact' ||
            !this.hasDates() ||
            this.nights <= 0 ||
            this.nights >= this.collectionMinNights
        ) {
            return null;
        }

        return `Choose at least ${this.collectionMinNights} nights for this stay.`;
    },

    handlePriceInput(field, rawValue) {
        const num = parseInt(rawValue.replace(/[^0-9]/g, '') || '0', 10);
        if (field === 'min') {
            this.filters.priceMin = Math.max(
                this.priceDefaults.min,
                Math.min(num, this.filters.priceMax)
            );
        } else {
            this.filters.priceMax = Math.min(
                this.priceDefaults.max,
                Math.max(num, this.filters.priceMin)
            );
        }
    },

    // Helper to calculate visibility deterministically
    updateVisibility() {
        const loading = this.ui.isLoading;
        const hasListings = this.results.listings.length > 0;
        const hasSearched = this.ui.hasSearched;
        const error = this.ui.error;

        // Simplified Loading Logic:
        // - Show skeleton whenever loading (any search type)
        // - Show results when we have listings AND not loading
        // - Show empty/initial state when not loading and no listings

        if (loading) {
            // Keep existing results visible during loading to prevent map hide/jank.
            // Only show skeleton when there are no listings yet.
            if (hasListings) {
                this.showResults = true;
                this.showSkeleton = false;
            } else {
                this.showResults = false;
                this.showSkeleton = true;
            }
        } else if (hasListings) {
            // Show results, hide skeleton
            this.showResults = true;
            this.showSkeleton = false;
        } else {
            // No listings and not loading - show empty/initial state
            this.showResults = hasSearched;
            this.showSkeleton = false;
        }

        // Error overrides everything
        if (error) {
            this.showResults = false;
            this.showSkeleton = false;
        }
    },

    init() {
        // The browser can restore this page from its back/forward cache after
        // a navigation. Reset the submit guard so the form is usable again.
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) this.ui.isSubmittingSearch = false;
        });
        this.parseUrlParams();
        this.loadRecentSearches();
        this.updateVisibility();
        // Auto-search when a destination or bounds are present
        if (window.location.pathname === this.searchPath) {
            if (this.where || this.boundsSearchEnabled) {
                this.performSearch({
                    resetPagination: false,
                    source: this.boundsSearchEnabled ? 'bounds' : 'user'
                });
            }
        }

        // Compute visualLabel reactively
        Alpine.effect(() => {
            if (!this.mapAreaLabel) {
                this.visualLabel = null;
            } else if (this._visualWhereMatch) {
                this.visualLabel =
                    this.where === this._visualWhereMatch
                        ? this.mapAreaLabel
                        : null;
            } else {
                this.visualLabel = !this.where ? this.mapAreaLabel : null;
            }
        });

        // Clear validation message when a destination is selected
        Alpine.effect(() => {
            if (
                this.whereData &&
                this.ui.validationMessage === 'Please select a destination'
            ) {
                this.ui.validationMessage = null;
            }
        });
    },

    // Parse URL params to restore search state
    parseUrlParams() {
        const params = new URLSearchParams(window.location.search);

        // Core params
        const locationState = parseLocationSearchState(params);
        if (locationState.where) this.where = locationState.where;
        if (locationState.whereData) this.whereData = locationState.whereData;
        if (params.get('bounds')) {
            const parts = params.get('bounds').split(',').map(Number);
            if (parts.length === 4 && parts.every((n) => !isNaN(n))) {
                const [north, south, east, west] = parts;
                this.bounds = { north, south, east, west };
                this.boundsSearchEnabled = true;
                this.mapAreaLabel = 'Homes in map area';
            }
        }
        if (params.get('dates')) this.dates = params.get('dates');
        if (params.get('dateMode') === 'flexible') {
            this.dateMode = 'flexible';
            this.flexibleMonth = params.get('flexibleMonth') || '';
            const stayRange = this.normalizeFlexibleStayRange(
                params.get('flexibleNights'),
                params.get('flexibleMaxNights')
            );
            this.flexibleNights = stayRange.minimumNights;
            this.flexibleMaxNights = stayRange.maximumNights;
        }
        if (params.get('sort')) this.sort = params.get('sort');
        if (params.get('guests'))
            this.guests = parseInt(params.get('guests')) || 0;
        if (params.get('adults'))
            this.adults = parseInt(params.get('adults')) || 0;
        if (params.get('children'))
            this.children = parseInt(params.get('children')) || 0;
        if (params.get('pets'))
            this.pets = this.clampPetCount(params.get('pets'));

        // Filter params
        if (params.get('propertyType')) {
            this.filters.propertyType = params
                .get('propertyType')
                .split(',')
                .filter(Boolean);
        }
        if (params.get('minPrice'))
            this.filters.priceMin =
                parseInt(params.get('minPrice')) || this.priceDefaults.min;
        if (params.get('maxPrice'))
            this.filters.priceMax =
                parseInt(params.get('maxPrice')) || this.priceDefaults.max;
        if (params.get('bedrooms'))
            this.filters.bedrooms = normalizeSearchBedrooms(
                params.get('bedrooms')
            );
        if (params.get('beds'))
            this.filters.beds = parseInt(params.get('beds')) || 1;
        if (params.get('bathrooms'))
            this.filters.bathrooms = parseInt(params.get('bathrooms')) || 1;
        if (params.get('amenities')) {
            this.filters.amenities = params
                .get('amenities')
                .split(',')
                .filter(Boolean);
        }
        if (params.get('accessibilityFeatures')) {
            this.filters.accessibilityFeatures = params
                .get('accessibilityFeatures')
                .split(',')
                .filter(Boolean);
        }

        // Pagination
        if (params.get('page')) {
            this.pagination.currentPage = parseInt(params.get('page')) || 1;
            this.pagination.offset =
                (this.pagination.currentPage - 1) * this.pagination.limit;
        }
    },

    loadRecentSearches() {
        try {
            const stored = JSON.parse(
                window.localStorage.getItem(RECENT_SEARCHES_KEY) || '[]'
            );
            if (!Array.isArray(stored)) return;

            const now = Date.now();
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            this.recentSearches = stored
                .filter(
                    (search) =>
                        search &&
                        typeof search.where === 'string' &&
                        search.where.trim() &&
                        Number.isFinite(Number(search.savedAt)) &&
                        now - Number(search.savedAt) <= RECENT_SEARCH_MAX_AGE_MS
                )
                .slice(0, RECENT_SEARCH_LIMIT)
                .map((search) => {
                    const normalized = { ...search };
                    if (normalized.dates) {
                        const parts = normalized.dates.split(' to ');
                        const start = new Date(`${parts[0].trim()}T00:00:00`);
                        if (!Number.isNaN(start.getTime()) && start < today) {
                            normalized.dates = '';
                        }
                    }

                    if (
                        normalized.dateMode === 'flexible' &&
                        normalized.flexibleMonth
                    ) {
                        const stayRange = this.normalizeFlexibleStayRange(
                            normalized.flexibleNights,
                            normalized.flexibleMaxNights
                        );
                        normalized.flexibleNights = stayRange.minimumNights;
                        normalized.flexibleMaxNights = stayRange.maximumNights;
                        const [year, month] = normalized.flexibleMonth
                            .split('-')
                            .map(Number);
                        const monthEnd = new Date(year, month, 0);
                        if (monthEnd < today) {
                            normalized.dateMode = 'exact';
                            normalized.flexibleMonth = '';
                        }
                    }

                    return normalized;
                });

            this.persistRecentSearches();
        } catch (error) {
            this.recentSearches = [];
            console.warn('Search Store: unable to read recent searches', error);
        }
    },

    persistRecentSearches() {
        try {
            window.localStorage.setItem(
                RECENT_SEARCHES_KEY,
                JSON.stringify(this.recentSearches)
            );
        } catch (error) {
            console.warn('Search Store: unable to save recent searches', error);
        }
    },

    saveRecentSearch() {
        if (!this.where) return;

        const search = {
            where: this.where,
            whereData: this.whereData
                ? JSON.parse(JSON.stringify(this.whereData))
                : null,
            dates: this.dateMode === 'exact' ? this.dates : '',
            dateMode: this.dateMode,
            flexibleMonth:
                this.dateMode === 'flexible' ? this.flexibleMonth : '',
            flexibleNights:
                this.dateMode === 'flexible' ? this.flexibleNights : 2,
            flexibleMaxNights:
                this.dateMode === 'flexible' ? this.flexibleMaxNights : 3,
            adults: this.adults,
            children: this.children,
            pets: this.petCount,
            guests: this.guests,
            bedrooms: this.filters.bedrooms,
            savedAt: Date.now()
        };
        const identity = JSON.stringify([
            search.where.trim().toLowerCase(),
            search.dates,
            search.dateMode,
            search.flexibleMonth,
            search.flexibleNights,
            search.flexibleMaxNights,
            search.adults,
            search.children,
            search.pets,
            search.bedrooms
        ]);

        this.recentSearches = [
            search,
            ...this.recentSearches.filter((item) => {
                const itemIdentity = JSON.stringify([
                    String(item.where || '')
                        .trim()
                        .toLowerCase(),
                    item.dates || '',
                    item.dateMode || 'exact',
                    item.flexibleMonth || '',
                    Number(item.flexibleNights) || 2,
                    Number(item.flexibleMaxNights) || 3,
                    Number(item.adults) || 1,
                    Number(item.children) || 0,
                    Number(item.pets) || 0,
                    normalizeSearchBedrooms(item.bedrooms)
                ]);
                return itemIdentity !== identity;
            })
        ].slice(0, RECENT_SEARCH_LIMIT);
        this.persistRecentSearches();
    },

    applyRecentSearch(search) {
        if (!search?.where) return;

        this.composerDestinationSlug = null;
        this.where = search.where;
        this.whereData = search.whereData
            ? JSON.parse(JSON.stringify(search.whereData))
            : null;
        this.dateMode = search.dateMode === 'flexible' ? 'flexible' : 'exact';
        this.dates = this.dateMode === 'exact' ? search.dates || '' : '';
        this.flexibleMonth =
            this.dateMode === 'flexible' ? search.flexibleMonth || '' : '';
        const stayRange = this.normalizeFlexibleStayRange(
            search.flexibleNights,
            search.flexibleMaxNights
        );
        this.flexibleNights = stayRange.minimumNights;
        this.flexibleMaxNights = stayRange.maximumNights;
        this.adults = Math.max(1, Number(search.adults) || 1);
        this.children = Math.max(0, Number(search.children) || 0);
        this.pets = this.clampPetCount(search.pets);
        this.filters.bedrooms = normalizeSearchBedrooms(search.bedrooms);
        this.updateGuests();
        this.ui.validationMessage = null;
    },

    clearRecentSearches() {
        this.recentSearches = [];
        this.persistRecentSearches();
    },

    // Helper: Build the search URL string from current state
    buildSearchUrl() {
        // Intentionally rebuild only supported search state. Marketing query
        // values are captured before Alpine starts and do not need to persist
        // in filter URLs (which also avoids UTM-specific cache variations).
        const params = new URLSearchParams();
        if (
            new URLSearchParams(window.location.search).get('prototype') ===
            'page-pricing'
        ) {
            params.set('prototype', 'page-pricing');
        }

        // Core params
        if (this.where && !this.isVirtualRentalCollectionScope())
            params.set('where', this.where);
        if (this.whereData?.lat != null && this.whereData?.lng != null) {
            params.set('lat', this.whereData.lat);
            params.set('lng', this.whereData.lng);
        }
        if (this.whereData?.placeType) {
            params.set('placeType', this.whereData.placeType);
        }
        if (this.bounds && this.mapAreaLabel) {
            params.set(
                'bounds',
                `${this.bounds.north},${this.bounds.south},${this.bounds.east},${this.bounds.west}`
            );
        }
        if (this.dateMode === 'flexible' && this.flexibleMonth) {
            params.set('dateMode', 'flexible');
            params.set('flexibleMonth', this.flexibleMonth);
            params.set('flexibleNights', this.flexibleNights);
            params.set('flexibleMaxNights', this.flexibleMaxNights);
        } else if (this.dates) {
            params.set('dates', this.dates);
        }
        if (this.sort !== 'relevance') params.set('sort', this.sort);
        if (this.guests > 1) params.set('guests', this.guests);
        if (this.adults > 1) params.set('adults', this.adults);
        if (this.children > 0) params.set('children', this.children);
        if (this.petCount > 0) params.set('pets', String(this.petCount));

        // Filter params (only if non-default)
        if (this.filters.propertyType.length) {
            params.set('propertyType', this.filters.propertyType.join(','));
        }
        if (this.filters.priceMin !== this.priceDefaults.min) {
            params.set('minPrice', this.filters.priceMin);
        }
        if (this.filters.priceMax !== this.priceDefaults.max) {
            params.set('maxPrice', this.filters.priceMax);
        }
        if (this.filters.bedrooms !== 1)
            params.set('bedrooms', this.filters.bedrooms);
        if (this.filters.beds > 1) params.set('beds', this.filters.beds);
        if (this.filters.bathrooms > 1)
            params.set('bathrooms', this.filters.bathrooms);
        if (this.filters.amenities.length) {
            params.set('amenities', this.filters.amenities.join(','));
        }
        if (this.filters.accessibilityFeatures.length) {
            params.set(
                'accessibilityFeatures',
                this.filters.accessibilityFeatures.join(',')
            );
        }

        // Pagination
        if (this.pagination.currentPage > 1) {
            params.set('page', this.pagination.currentPage);
        }

        const queryString = params.toString();
        return queryString
            ? `${this.searchPath}?${queryString}`
            : this.searchPath;
    },

    appendAvailabilityParams(params) {
        appendSearchAvailabilityParams(params, this);
    },

    // Sync current state to URL history (without reload)
    syncUrlState() {
        const url = this.buildSearchUrl();
        try {
            window.history.replaceState({}, '', url);
        } catch (error) {
            console.warn('Search Store: unable to sync URL state', error);
        }
    },

    trackViewItemList() {
        try {
            viewItemList(this);
        } catch (error) {
            console.warn('Search Store: unable to track view_item_list', error);
        }
    },

    trackUrgencyImpression() {
        try {
            availabilityScarcityImpression(this);
        } catch (error) {
            console.warn(
                'Search Store: unable to track availability scarcity impression',
                error
            );
        }
    },

    dismissUrgency() {
        this.urgencyDismissed = true;
        try {
            availabilityScarcityDismissed(this);
        } catch (error) {
            console.warn(
                'Search Store: unable to track availability scarcity dismissal',
                error
            );
        }
    },

    // Submit search: Redirect or update in-place
    submitSearch(source) {
        // Validation: Require a selected destination (whereData) or at least text input
        if (!this.where && !this.whereData) {
            this.ui.validationMessage = 'Please select a destination';
            setTimeout(() => {
                this.ui.validationMessage = null;
            }, 3000);
            return;
        }

        if (this.collectionStayValidationMessage) {
            this.ui.validationMessage = this.collectionStayValidationMessage;
            return;
        }

        if (this.dateMode === 'flexible') {
            if (!this.flexibleMonth) {
                this.ui.validationMessage = 'Choose a month for your trip.';
                return;
            }
        }

        // A typed query can match rental names as well as places. Preserve it
        // for the search endpoint instead of navigating to the first suggestion.
        if (this.where && !this.whereData) {
            this.whereData = { lat: null, lng: null, placeType: 'listing' };
        }

        console.log('Search Store: submitSearch called', { where: this.where });

        // Cancel any in-flight request to prevent stale results
        if (this.currentAbortController) {
            this.currentAbortController.abort();
            this.currentAbortController = null;
        }

        // Cancel pending timeout (if user changed search before 750ms elapsed)
        if (window.Alpine?.store?.('searchMap')?.cancelPendingBoundsSearch) {
            window.Alpine.store('searchMap').cancelPendingBoundsSearch();
        }

        // Clear bounds when user changes search (map will auto-fit to new results)
        this.clearBounds();
        this.saveRecentSearch();

        // If we are not on the search page, redirect
        if (window.location.pathname !== this.searchPath) {
            const url = this.buildSearchUrl();
            console.log('Search Store: Redirecting to', url);
            this.ui.isSubmittingSearch = true;
            window.location.href = url;
            return;
        }

        // Track search initiation
        searchInitiated(this);

        // If we are already on search page, update URL and fetch
        this.syncUrlState();
        this.ui.isSubmittingSearch = source !== 'sort';
        this.performSearch({
            resetPagination: true,
            source: source || 'destination'
        });
    },

    // Navigate to search page with current store state (no validation)
    goToSearch() {
        window.location.href = this.buildSearchUrl();
    },

    // Main search method (two-phase)
    async performSearch(options = {}) {
        const {
            resetPagination = true,
            requestId = null,
            source = 'user'
        } = options;

        if (window.__AVARI_DEBUG_MAP_SEARCH) {
            console.log('[SEARCH] performSearch called', { requestId, source });
        }

        // Track the latest request ID we've issued
        // Ensure we have a request ID for tracking race conditions
        // Use high-precision timing + random to ensure uniqueness even in rapid-fire scenarios
        const currentRequestId =
            requestId ||
            `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        this.lastIssuedRequestId = currentRequestId;

        // Cancel any in-flight request
        if (this.currentAbortController) {
            this.currentAbortController.abort();
        }

        // Create new AbortController for this request
        this.currentAbortController = new AbortController();
        const signal = this.currentAbortController.signal;

        if (resetPagination) {
            this.pagination.currentPage = 1;
            this.pagination.offset = 0;
        }

        this.ui.isLoading = true;
        this.ui.error = null;
        this.ui.errorTitle = null;
        this.ui.errorKind = null;
        this.ui.availabilityPendingPhase = null;
        if (shouldResetUrgencyDismissal(source)) {
            this.urgencyDismissed = false;
        }
        // Update visibility immediately (shows skeleton)
        this.updateVisibility();
        this.executedWhere = this.where; // Snapshot the query

        // CRITICAL: Set search source BEFORE clearing results
        // This prevents race condition where map watcher fires with old source value
        // causing unwanted fitBounds() calls during bounds-based searches
        this._lastSearchSource = source;
        if (source !== 'pagination') {
            prototypeMapPrices.reset();
            this.results.pricingContext = null;
            this.results.mapPriceStates = {};
        }

        // Clear stale filter-preview count whenever the search isn't driven by
        // filter changes (e.g. destination/bounds/dates/pagination). Without
        // this the modal would show the previous location's count on first
        // open until the user touched a filter input.
        if (source !== 'filters') {
            this.liveFilterCount = null;
        }

        try {
            const {
                listings,
                count,
                listingPrices,
                pricingMode,
                pricingContext,
                priceFallbackDiagnostics,
                listingStays,
                mapPoints,
                rentalCollection,
                urgency,
                urgencyDiagnostics
            } = await this.fetchListings(signal);

            // CRITICAL: Only accept if this is still the latest request we issued
            // Ignores out-of-order arrivals (stale responses)
            if (requestId !== null && requestId !== this.lastIssuedRequestId) {
                if (window.__AVARI_DEBUG_MAP_SEARCH) {
                    console.log(
                        '[SEARCH] Ignoring stale response',
                        requestId,
                        'vs latest',
                        this.lastIssuedRequestId
                    );
                }
                return;
            }

            if (window.__AVARI_DEBUG_MAP_SEARCH) {
                console.log(
                    '[SEARCH] Accepted request',
                    requestId,
                    'source:',
                    source,
                    'listings:',
                    listings.length
                );
            }

            const pricingContextChanged =
                this.results.pricingContext !== pricingContext;
            prototypeMapPrices.setContext(pricingContext, listingPrices || {});
            this.results.pricingMode = pricingMode;
            this.results.pricingContext = pricingContext;
            this.results.mapPriceStates = {};

            if (!listings.length) {
                this.results.listings = [];
                this.results.mapPoints = [];
                this.results.listingPrices = {};
                this.results.listingStays = {};
                this.results.totalCount = 0;
                this.results.filteredCount = 0;
                this.results.urgency =
                    urgency || this.emptyUrgency('not_returned');
                this.results.urgencyDiagnostics = urgencyDiagnostics || null;
                this.pagination.totalPages = 0;
                this.ui.hasSearched = true;
                this.syncUrlState();
                this.trackViewItemList();
                return;
            }

            this.results.listingStays = listingStays || {};

            // A rebuilt context can change availability even during pagination.
            if (source !== 'pagination' || pricingContextChanged) {
                this.results.mapPoints = mapPoints;
            }

            // Store listings (Server-side pagination: "listings" is just the current page)
            this.results.listings = listings;
            // CRITICAL: use API total count for pagination, NOT array length
            this.results.totalCount = count || listings.length;
            this.results.filteredCount = count || listings.length;
            this.results.urgency = urgency || this.emptyUrgency('not_returned');
            this.results.urgencyDiagnostics = urgencyDiagnostics || null;
            if (
                shouldTrackUrgencyImpression(
                    this.results.urgency.enabled,
                    this.urgencyDismissed
                )
            ) {
                this.trackUrgencyImpression();
            }

            // Phase 2: Get availability/pricing if dates are selected, or when
            // a virtual scope returns season-priced listings inline (rental collections).
            if (
                this.hasAvailabilityDates() ||
                (listingPrices && Object.keys(listingPrices).length > 0)
            ) {
                let constraintPrices;
                let availableIds;

                if (
                    pricingMode === 'page' ||
                    (listingPrices && Object.keys(listingPrices).length > 0)
                ) {
                    // Prices were included inline from actionIndex — skip separate API call
                    constraintPrices = listingPrices;
                    availableIds = Object.keys(listingPrices).map(String);
                } else {
                    // Fallback: fetch constraints separately (browse or legacy)
                    const candidateIds = listings
                        .map((l) => String(l.guestyListingId))
                        .filter(Boolean);

                    const constraints = await this.fetchConstraints(
                        candidateIds,
                        priceFallbackDiagnostics
                    );
                    constraintPrices = constraints.listingPrices || {};
                    availableIds =
                        constraints.availableListingIds?.map(String) ?? null;
                }

                this.results.listingPrices =
                    pricingMode === 'page'
                        ? { ...prototypeMapPrices.prices }
                        : constraintPrices;
                this.results.pricedNights =
                    this.nights ||
                    rentalCollection?.minNights ||
                    this.results.pricedNights ||
                    0;

                // Filter listings to only available ones
                let filteredListings = listings;
                if (availableIds) {
                    filteredListings = listings.filter((l) => {
                        const id = String(l.guestyListingId);
                        return availableIds.includes(id);
                    });
                } else {
                    filteredListings = listings.filter((l) => {
                        const id = String(l.guestyListingId);
                        return this.results.listingPrices[id] !== undefined;
                    });
                }

                // Optimize: Only update if the list actually changed to prevent double-triggering map watcher
                const prevIds = this.results.listings
                    .map((l) => l.guestyListingId || l.id)
                    .join(',');
                const newIds = filteredListings
                    .map((l) => l.guestyListingId || l.id)
                    .join(',');

                if (prevIds !== newIds) {
                    this.results.listings = filteredListings;
                }
            } else {
                this.results.listingPrices = {};
                this.results.listingStays = {};
                this.results.pricedNights = 0;
            }

            // Update pagination
            this.pagination.totalPages = Math.ceil(
                this.results.filteredCount / this.pagination.limit
            );

            this.ui.hasSearched = true;
            this.syncUrlState();
            this.trackViewItemList();
        } catch (error) {
            // Don't show error for cancelled requests
            if (error.name === 'AbortError') {
                if (window.__AVARI_DEBUG_MAP_SEARCH) {
                    console.log('[SEARCH] Request cancelled (AbortError)');
                }
                return;
            }

            console.error('Search error:', error);
            if (error.name === 'MinimumStayValidationError') {
                this.ui.errorTitle = 'Choose a longer stay';
                this.ui.errorKind = 'validation';
                this.ui.error = error.message;
                this.ui.validationMessage = error.message;
            } else if (
                error.name === 'AvailabilityUnavailableError' ||
                error.name === 'RentalCollectionAvailabilityPendingError'
            ) {
                this.ui.errorTitle =
                    'Live availability is temporarily unavailable';
                this.ui.errorKind = 'availability';
                this.ui.error = error.message;
            } else if (error instanceof HttpResponseError) {
                this.ui.errorTitle = 'Search is temporarily unavailable';
                this.ui.errorKind = 'generic';
                this.ui.error = error.message;
            } else {
                this.ui.errorTitle = 'Something went wrong';
                this.ui.errorKind = 'generic';
                this.ui.error =
                    'Unable to load search results. Please try again.';
            }
        } finally {
            // Only turn off loading if this was the last request issued
            if (this.lastIssuedRequestId === currentRequestId) {
                this.ui.isLoading = false;
                this.ui.availabilityPendingPhase = null;
                this.ui.isSubmittingSearch = false;
            }
            // Always update visibility at the end
            this.updateVisibility();
        }
    },

    // Phase 1: Fetch listings from search index
    async fetchListings(signal) {
        const params = new URLSearchParams();

        // Market/location (maps "where" to "market")
        if (this.where && !this.isVirtualRentalCollectionScope()) {
            params.set('market', this.where);
        }

        // Coordinate-based proximity search
        if (this.whereData?.lat != null && this.whereData?.lng != null) {
            params.set('lat', this.whereData.lat);
            params.set('lng', this.whereData.lng);
        }

        // Place type (region | place | locality | neighborhood) — lets the
        // backend route the search to the appropriate strategy.
        if (this.whereData?.placeType) {
            params.set('placeType', this.whereData.placeType);
        }

        // Sort order
        if (this.sort !== 'relevance') {
            params.set('sort', this.sort);
        }

        // Dates switch the backend into verified availability and total-price mode.
        this.appendAvailabilityParams(params);

        // Bedrooms filter - pass as 'bedrooms' parameter (maps to numberOfBedrooms in API)
        if (this.filters.bedrooms !== 1) {
            params.set('bedrooms', this.filters.bedrooms);
        }

        // Beds filter
        if (this.filters.beds > 1) {
            params.set('beds', this.filters.beds);
        }

        // Bathrooms filter
        if (this.filters.bathrooms > 1) {
            params.set('bathrooms', this.filters.bathrooms);
        }

        // Amenities filter
        if (this.filters.amenities.length) {
            params.set('amenities', this.filters.amenities.join(','));
        }

        // Property Type filter
        if (this.filters.propertyType.length) {
            params.set('propertyType', this.filters.propertyType.join(','));
        }

        // Accessibility Features filter
        if (this.filters.accessibilityFeatures.length) {
            params.set(
                'accessibilityFeatures',
                this.filters.accessibilityFeatures.join(',')
            );
        }

        // Price filters
        if (this.filters.priceMin !== this.priceDefaults.min) {
            params.set('minPrice', this.filters.priceMin);
        }
        if (this.filters.priceMax !== this.priceDefaults.max) {
            params.set('maxPrice', this.filters.priceMax);
        }

        // Guest params (passed through to card URLs for detail page)
        if (this.guests > 1) params.set('guests', this.guests);
        if (this.adults > 1) params.set('adults', this.adults);
        if (this.children > 0) params.set('children', this.children);
        if (this.petCount > 0) params.set('pets', String(this.petCount));

        // Map bounds filter (for search-as-you-move)
        if (this.bounds) {
            params.set('bounds', JSON.stringify(this.bounds));
        }

        // Server-side Pagination
        params.set('limit', this.pagination.limit);
        params.set('offset', this.pagination.offset);

        const endpoint = this.endpoint || '/guesty/search/index';
        if (
            endpoint === '/guesty/search/index' &&
            new URLSearchParams(window.location.search).get('prototype') ===
                'page-pricing'
        ) {
            params.set('pricingMode', 'page');
            if (this.results.pricingContext)
                params.set('pricingContext', this.results.pricingContext);
        }
        const separator = endpoint.includes('?') ? '&' : '?';
        const url = `${endpoint}${separator}${params.toString()}`;
        console.log('Search Store: Fetching listings', url);
        console.log('Search StoreParams:', {
            where: this.where,
            market: params.get('market'),
            params: params.toString()
        });

        const outcome = await pollRentalCollectionAvailability({
            signal,
            onPending: (_payload, _attempt, phase) => {
                if (!signal.aborted) {
                    this.ui.availabilityPendingPhase = phase;
                }
            },
            request: async () => {
                const response = await fetch(url, {
                    method: 'GET',
                    credentials: 'include',
                    signal, // Enable request cancellation
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    }
                });
                const data = await readJsonResponse(response);
                signal?.throwIfAborted();

                if (
                    ['partial', 'unavailable', 'mutex_failed'].includes(
                        data?.availability?.state
                    )
                ) {
                    const error = new Error(
                        data.message ||
                            'We could not verify your selected dates and pricing. Try again or browse without dates.'
                    );
                    error.name = 'AvailabilityUnavailableError';
                    throw error;
                }
                if (
                    response.status === 422 &&
                    data?.code === 'rental_collection_minimum_stay'
                ) {
                    const error = new Error(data.error);
                    error.name = 'MinimumStayValidationError';
                    throw error;
                }
                assertJsonResponse(response, data);
                if (!data.success) {
                    throw new Error(data.error || 'Failed to fetch listings');
                }

                return data;
            }
        });

        if (outcome.state !== 'ready') {
            // A 202 pending response intentionally has an empty results array.
            // Do not let the normal empty-results path erase the current list.
            const error = new Error(
                'Availability is taking longer than expected. Please try again.'
            );
            error.name = 'RentalCollectionAvailabilityPendingError';
            throw error;
        }

        const data = outcome.payload;

        // Capture backend message (e.g., "Please select a destination")
        if (data.message) {
            this.ui.backendMessage = data.message;
        } else {
            this.ui.backendMessage = null;
        }

        const results = data.results || [];

        const inlinePrices = data.listingPrices;
        const priceState = !Object.hasOwn(data, 'listingPrices')
            ? 'missing'
            : inlinePrices === null
              ? 'null'
              : !inlinePrices ||
                  typeof inlinePrices !== 'object' ||
                  Array.isArray(inlinePrices)
                ? 'invalid'
                : Object.keys(inlinePrices).length === 0
                  ? 'empty'
                  : 'present';

        // Debug: Log first listing structure to see what fields are available
        if (window.__AVARI_DEBUG_MAP_SEARCH && results.length > 0) {
            console.log('Sample listing structure (Phase 1):', {
                id:
                    results[0].guestyListingId ||
                    results[0]._id ||
                    results[0].id,
                bedrooms: results[0].bedrooms,
                accommodates: results[0].accommodates,
                beds: results[0].beds,
                bathrooms: results[0].bathrooms,
                images: results[0].images?.length,
                pictures: results[0].pictures?.length,
                allKeys: Object.keys(results[0])
            });
        }

        return {
            listings: results,
            count: data.count || 0,
            listingPrices: data.listingPrices || null,
            pricingMode: data.pricingMode || null,
            pricingContext: data.pricingContext || null,
            priceFallbackDiagnostics: {
                indexMode:
                    params.get('dateMode') === 'flexible'
                        ? 'flexible'
                        : params.has('dates')
                          ? 'exact'
                          : 'browse',
                priceState,
                indexRequestId: data._timing?.request_id || '',
                indexCache: data._timing?.app_cache || ''
            },
            listingStays: data.listingStays || null,
            mapPoints: data.mapPoints || [],
            rentalCollection: data.rentalCollection || null,
            urgency: data.urgency || null,
            urgencyDiagnostics: data.urgencyDiagnostics || null
        };
    },

    // Phase 2: Fetch availability/pricing constraints
    async fetchConstraints(candidateListingIds, priceFallbackDiagnostics = {}) {
        const [checkIn, checkOut] = this.parseDates();

        console.log(
            'fetchConstraints: dates=',
            this.dates,
            'parsed=',
            checkIn,
            checkOut
        );

        if (!checkIn || !checkOut) {
            return {
                ok: true,
                count: candidateListingIds.length,
                listingPrices: {}
            };
        }

        const params = new URLSearchParams();
        params.set('checkIn', checkIn);
        params.set('checkOut', checkOut);
        params.set('guests', this.guests);
        if (this.pets > 0) params.set('pets', String(this.pets));

        // Price filters
        if (this.filters.priceMin !== this.priceDefaults.min) {
            params.set('minPrice', this.filters.priceMin);
        }
        if (this.filters.priceMax !== this.priceDefaults.max) {
            params.set('maxPrice', this.filters.priceMax);
        }

        // Bedrooms/beds/bathrooms filters (for API-level filtering in Phase 2)
        console.log(
            'fetchConstraints: filters.bedrooms=',
            this.filters.bedrooms
        );
        if (this.filters.bedrooms !== 1) {
            params.set('bedrooms', this.filters.bedrooms);
        }
        if (this.filters.beds > 1) {
            params.set('beds', this.filters.beds);
        }
        if (this.filters.bathrooms > 1) {
            params.set('bathrooms', this.filters.bathrooms);
        }

        // Candidate IDs
        candidateListingIds.forEach((id) => {
            params.append('candidateListingIds[]', id);
        });

        if (priceFallbackDiagnostics.indexMode) {
            params.set('fallbackIndexMode', priceFallbackDiagnostics.indexMode);
            params.set(
                'fallbackPriceState',
                priceFallbackDiagnostics.priceState
            );
            params.set(
                'fallbackIndexRequestId',
                priceFallbackDiagnostics.indexRequestId
            );
            params.set(
                'fallbackIndexCache',
                priceFallbackDiagnostics.indexCache
            );
            params.set(
                'fallbackClientMode',
                this.hasAvailabilityDates() ? 'dated' : 'browse'
            );
        }

        const url = `/guesty/search/constraints?${params.toString()}`;
        console.log('fetchConstraints: URL=', url);

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        console.log('fetchConstraints: status=', response.status);

        const data = await response.json();

        if (
            ['partial', 'unavailable', 'mutex_failed'].includes(
                data.availability?.state
            )
        ) {
            const error = new Error(
                data.error || 'Live availability is temporarily unavailable.'
            );
            error.name = 'AvailabilityUnavailableError';
            throw error;
        }

        if (!response.ok) {
            console.error('fetchConstraints: error response=', data);
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        console.log('fetchConstraints: response data=', data);

        if (!data.ok) {
            throw new Error('Failed to fetch availability');
        }

        return data;
    },

    // Fetch popup HTML for a single listing (lazy-load for non-page markers)
    async fetchPopup(entryId) {
        const response = await fetch(`/guesty/search/popup?id=${entryId}`, {
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (!data.success) {
            throw new Error(data.error || 'Failed to fetch popup');
        }

        return data.popupHtml;
    },

    // Fetch popup HTML for multiple listings in a single request
    async fetchPopupBatch(entryIds) {
        const params = new URLSearchParams();
        entryIds.forEach((id) => params.append('ids[]', id));

        const response = await fetch(
            `/guesty/search/popup-batch?${params.toString()}`,
            {
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (!data.success) {
            throw new Error(data.error || 'Failed to fetch popup batch');
        }

        return data.popups || {};
    },

    // Pagination methods
    async goToPage(page) {
        if (page < 1 || page > this.pagination.totalPages) return;
        if (page === this.pagination.currentPage) return;

        this.pagination.currentPage = page;
        this.pagination.offset = (page - 1) * this.pagination.limit;

        // Server-side pagination: Fetch new page
        this.ui.isLoadingMore = true;
        await this.performSearch({
            resetPagination: false,
            source: 'pagination'
        });
        this.ui.isLoadingMore = false;

        // Scroll to top of results
        window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    nextPage() {
        this.goToPage(this.pagination.currentPage + 1);
    },

    prevPage() {
        this.goToPage(this.pagination.currentPage - 1);
    },

    // Apply filters (called from modal)
    applyFilters() {
        this.cancelFilterCount();

        // Cancel any in-flight request to prevent stale results
        if (this.currentAbortController) {
            this.currentAbortController.abort();
            this.currentAbortController = null;
        }

        // Cancel pending timeout
        if (window.Alpine?.store?.('searchMap')?.cancelPendingBoundsSearch) {
            window.Alpine.store('searchMap').cancelPendingBoundsSearch();
        }

        this.performSearch({ resetPagination: true, source: 'filters' });
        Alpine.store('modals').close('filters-modal');
    },

    // Set map bounds (called when user drags/zooms map)
    setBounds(bounds) {
        this.bounds = bounds;
        this.boundsSearchEnabled = true;
    },

    // Clear bounds (called when user changes filters/destination)
    clearBounds() {
        this.bounds = null;
        this.boundsSearchEnabled = false;
        this.mapAreaLabel = null;
        this._visualWhereMatch = null;
    },

    // Search within current map bounds
    async searchInBounds(requestId) {
        // Clear destination-based search state
        this.composerDestinationSlug = null;
        this.where = '';
        this.whereData = null;
        this.mapAreaLabel = 'Homes in map area';
        this._visualWhereMatch = null;

        this.pagination.currentPage = 1;
        this.pagination.offset = 0;
        await this.performSearch({
            resetPagination: false,
            requestId,
            source: 'bounds'
        });
    },

    // Reset filters to defaults
    resetFilters() {
        // Avoid replacing empty arrays with new reactive values when there is
        // nothing to clear. Alpine would otherwise schedule a duplicate count
        // request for the unchanged filter state.
        if (this.getActiveFilterCount() === 0) return;

        this.filters.propertyType = [];
        this.filters.priceMin = this.priceDefaults.min;
        this.filters.priceMax = this.priceDefaults.max;
        this.filters.bedrooms = 1;
        this.filters.beds = 1;
        this.filters.bathrooms = 1;
        this.filters.amenities = [];
        this.filters.accessibilityFeatures = [];
    },

    // Live filter count: fetch count-only from backend (skips hydration)
    async fetchFilterCount() {
        const previousStatus = this.filterCountStatus;
        if (this._filterCountController) {
            this._filterCountController.abort('filters-replaced');
        }
        const controller = new AbortController();
        this._filterCountController = controller;
        this.filterCountLoading = true;
        this.filterCountStatus = 'loading';
        let sawPending = false;

        try {
            const params = new URLSearchParams();
            if (this.where && !this.isVirtualRentalCollectionScope())
                params.set('market', this.where);
            if (this.whereData?.lat != null && this.whereData?.lng != null) {
                params.set('lat', this.whereData.lat);
                params.set('lng', this.whereData.lng);
            }
            if (this.whereData?.placeType) {
                params.set('placeType', this.whereData.placeType);
            }
            this.appendAvailabilityParams(params);
            if (this.guests > 1) params.set('guests', this.guests);
            if (this.adults > 1) params.set('adults', this.adults);
            if (this.children > 0) params.set('children', this.children);
            if (this.pets > 0) params.set('pets', String(this.pets));
            if (this.filters.bedrooms !== 1)
                params.set('bedrooms', this.filters.bedrooms);
            if (this.filters.beds > 1) params.set('beds', this.filters.beds);
            if (this.filters.bathrooms > 1)
                params.set('bathrooms', this.filters.bathrooms);
            if (this.filters.amenities.length)
                params.set('amenities', this.filters.amenities.join(','));
            if (this.filters.propertyType.length)
                params.set('propertyType', this.filters.propertyType.join(','));
            if (this.filters.accessibilityFeatures.length)
                params.set(
                    'accessibilityFeatures',
                    this.filters.accessibilityFeatures.join(',')
                );
            if (this.filters.priceMin !== this.priceDefaults.min)
                params.set('minPrice', this.filters.priceMin);
            if (this.filters.priceMax !== this.priceDefaults.max)
                params.set('maxPrice', this.filters.priceMax);
            if (this.petCount > 0) params.set('pets', String(this.petCount));
            if (this.bounds) params.set('bounds', JSON.stringify(this.bounds));
            params.set('countOnly', 'true');

            const endpoint = this.endpoint || '/guesty/search/index';
            const separator = endpoint.includes('?') ? '&' : '?';
            const url = `${endpoint}${separator}${params}`;
            const outcome = await pollRentalCollectionAvailability({
                signal: controller.signal,
                request: async () => {
                    const resp = await fetch(url, {
                        signal: controller.signal,
                        credentials: 'include',
                        headers: {
                            Accept: 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    });
                    const data = await resp.json();

                    if (
                        ['partial', 'unavailable', 'mutex_failed'].includes(
                            data.availability?.state
                        )
                    ) {
                        const error = new Error(
                            data.message ||
                                'Live availability is temporarily unavailable.'
                        );
                        error.name = 'AvailabilityUnavailableError';
                        throw error;
                    }
                    if (!resp.ok) {
                        throw new Error(`HTTP error! status: ${resp.status}`);
                    }
                    if (!data.success) {
                        throw new Error(
                            data.error || 'Failed to fetch filter count'
                        );
                    }

                    return data;
                },
                onPending: () => {
                    if (this._filterCountController !== controller) return;

                    // Unknown availability must not fall back to a numeric
                    // count from the previous completed filter request.
                    sawPending = true;
                    this.liveFilterCount = null;
                    this.filterCountStatus = 'pending';
                }
            });

            if (this._filterCountController !== controller) return;

            if (
                outcome.state === 'ready' &&
                typeof outcome.payload?.count === 'number'
            ) {
                this.liveFilterCount = outcome.payload.count;
                this.filterCountStatus = 'ready';
            } else {
                this.liveFilterCount = null;
                this.filterCountStatus = 'unavailable';
            }
        } catch (e) {
            if (!controller.signal.aborted && e.name !== 'AbortError') {
                console.error('Filter count error:', e);
                if (this._filterCountController === controller) {
                    if (
                        e.name === 'AvailabilityUnavailableError' ||
                        sawPending ||
                        this.liveFilterCount === null
                    ) {
                        this.liveFilterCount = null;
                        this.filterCountStatus = 'unavailable';
                    } else {
                        // Preserve a previously completed count when an
                        // ordinary replacement request fails before pending.
                        this.filterCountStatus =
                            previousStatus === 'ready' ? 'ready' : 'idle';
                    }
                }
            }
        } finally {
            // An older aborted poll must not clear the loading state owned by
            // the replacement request.
            if (this._filterCountController === controller) {
                this._filterCountController = null;
                this.filterCountLoading = false;
            }
        }
    },

    cancelFilterCount() {
        this._debouncedFilterCount?.cancel?.();
        this._filterCountController?.abort('filter-context-closed');
        this._filterCountController = null;
        this.filterCountLoading = false;
        if (
            this.filterCountStatus === 'loading' ||
            this.filterCountStatus === 'pending'
        ) {
            this.filterCountStatus = 'idle';
        }
    },

    // Debounce rapid user changes before the first request. Any later retries
    // for a backend "pending" response are owned by fetchFilterCount's poll.
    scheduleFilterCount() {
        if (!this._debouncedFilterCount) {
            let timer = null;
            const debouncedFilterCount = () => {
                clearTimeout(timer);

                // The completed count belongs to the previous context. Mark
                // it unknown during the debounce so the modal never presents
                // that stale value as current.
                this._filterCountController?.abort('filter-preview-replaced');
                this._filterCountController = null;
                this.liveFilterCount = null;
                this.filterCountLoading = true;
                this.filterCountStatus = 'loading';

                timer = setTimeout(() => {
                    timer = null;
                    this.fetchFilterCount();
                }, 300);
            };
            debouncedFilterCount.cancel = () => {
                clearTimeout(timer);
                timer = null;
            };
            this._debouncedFilterCount = debouncedFilterCount;
        }

        this._debouncedFilterCount();
    },

    // Get count of active (non-default) filters
    getActiveFilterCount() {
        let count = 0;
        const countSelections = this.activeFilterCountMode === 'grouped';

        if (this.filters.propertyType.length)
            count += countSelections ? this.filters.propertyType.length : 1;
        if (this.filters.priceMin !== this.priceDefaults.min) count++;
        if (this.filters.priceMax !== this.priceDefaults.max) count++;
        if (this.filters.bedrooms !== 1) count++;
        if (this.filters.beds > 1) count++;
        if (this.filters.bathrooms > 1) count++;
        if (countSelections) {
            count += this.getGroupedFilterSelectionCount([
                ...this.filters.amenities,
                ...this.filters.accessibilityFeatures
            ]);
        } else {
            if (this.filters.amenities.length) count++;
            if (this.filters.accessibilityFeatures.length) count++;
        }
        return count;
    },

    getGroupedFilterSelectionCount(values = []) {
        const remaining = new Set(values.filter(Boolean));
        let count = 0;

        this.filterCountGroups.forEach((group) => {
            const groupValues = Array.isArray(group)
                ? group.filter(Boolean)
                : [];
            if (groupValues.some((value) => remaining.has(value))) {
                count++;
                groupValues.forEach((value) => remaining.delete(value));
            }
        });

        return count + remaining.size;
    },

    updateGuests() {
        const adults = Number.isFinite(Number(this.adults))
            ? Number(this.adults)
            : 0;
        const children = Number.isFinite(Number(this.children))
            ? Number(this.children)
            : 0;
        this.pets = this.clampPetCount(this.pets);
        this.adults = Math.max(0, adults);
        this.children = Math.max(0, children);
        this.guests = this.adults + this.children;
    },

    clampPetCount(value) {
        const pets = Number.isFinite(Number(value))
            ? Math.floor(Number(value))
            : 0;
        return Math.max(0, Math.min(2, pets));
    },

    reset() {
        prototypeMapPrices.reset();
        this.composerDestinationSlug = null;
        this.where = '';
        this.whereData = null;
        this.mapAreaLabel = null;
        this._visualWhereMatch = null;
        this.dates = '';
        this.dateMode = 'exact';
        this.flexibleMonth = '';
        this.flexibleNights = 2;
        this.flexibleMaxNights = 3;
        this.adults = 1;
        this.children = 0;
        this.pets = 0;
        this.updateGuests();
        this.resetFilters();
        this.clearBounds();
        this.results = {
            pricingMode: null,
            pricingContext: null,
            mapPriceStates: {},
            mapPriceVersion: 0,
            listings: [],
            mapPoints: [],
            listingPrices: {},
            listingStays: {},
            pricedNights: 0,
            totalCount: 0,
            filteredCount: 0,
            urgencyDiagnostics: null,
            urgency: this.emptyUrgency()
        };
        this.urgencyDismissed = false;
        this.pagination = {
            currentPage: 1,
            limit: 12,
            offset: 0,
            totalPages: 1
        };
        this.ui.hasSearched = false;
        this.ui.error = null;
        this.ui.errorTitle = null;
        this.ui.errorKind = null;
        this.syncUrlState();
    },

    browseWithoutDates() {
        this.dates = '';
        this.dateMode = 'exact';
        this.flexibleMonth = '';
        this.flexibleNights = 2;
        this.flexibleMaxNights = 3;
        this.ui.error = null;
        this.ui.errorTitle = null;
        this.ui.errorKind = null;
        this.liveFilterCount = null;
        this.syncUrlState();
        return this.performSearch({
            resetPagination: true,
            source: 'availability-fallback'
        });
    },

    matches(option, query) {
        const parsedQuery = normalize(query);
        if (!parsedQuery) return true;
        return normalize(option).includes(parsedQuery);
    },

    // Helper: Formatted guests label (e.g. "1 guest", "3 guests")
    get guestsLabel() {
        if (this.guests <= 0) return '';
        const guestLabel =
            this.guests === 1 ? '1 guest' : `${this.guests} guests`;
        const pets = this.petCount;
        if (pets <= 0) return guestLabel;

        const petLabel = pets === 1 ? '1 Pet' : `${pets} Pets`;
        return `${guestLabel}, ${petLabel}`;
    },

    get petCount() {
        if (this.pets === true) return 1;
        if (this.pets === false || this.pets == null) return 0;
        return Math.min(2, Math.max(0, parseInt(this.pets, 10) || 0));
    },

    get rentalCollectionGuestsLabel() {
        const guestLabel =
            this.guests === 1 ? '1 Guest' : `${this.guests} Guests`;
        const pets = this.petCount;

        if (pets <= 0) {
            return guestLabel;
        }

        return `${guestLabel}, ${pets} ${pets === 1 ? 'Pet' : 'Pets'}`;
    },

    // Helper: Check if dates are selected
    hasDates() {
        return this.dates && this.dates.includes(' to ');
    },

    hasFlexibleDates() {
        return this.dateMode === 'flexible' && Boolean(this.flexibleMonth);
    },

    hasAvailabilityDates() {
        return Boolean(this.hasDates() || this.hasFlexibleDates());
    },

    hasDateInput() {
        return Boolean(
            String(this.dates || '').trim() ||
            (this.dateMode === 'flexible' && this.flexibleMonth)
        );
    },

    // Helper: Parse dates from "YYYY-MM-DD to YYYY-MM-DD" format
    parseDates() {
        if (!this.hasDates()) return [null, null];
        const parts = this.dates.split(' to ');
        return [parts[0]?.trim(), parts[1]?.trim()];
    },

    parseDateOnly(value) {
        if (typeof value !== 'string') return null;
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return null;

        const year = Number(match[1]);
        const month = Number(match[2]) - 1;
        const day = Number(match[3]);
        return new Date(year, month, day);
    },

    // Helper: Get number of nights from selected dates
    get nights() {
        const [checkIn, checkOut] = this.parseDates();
        if (!checkIn || !checkOut) return 0;
        const msPerDay = 86400000;
        const start = this.parseDateOnly(checkIn);
        const end = this.parseDateOnly(checkOut);
        if (!start || !end) return 0;
        return Math.round((end - start) / msPerDay);
    },

    // Helper: Price label based on nights the prices were fetched for
    get priceLabel() {
        const n = this.results.pricedNights;
        if (n > 1) return '/ ' + n + ' nights';
        if (n === 1) return 'total';
        return '/ night';
    },

    get pricePrefix() {
        return this.isVirtualRentalCollectionScope() ? 'From ' : '';
    },

    // Helper: Get listing price from constraints
    async fetchPrototypeMapPrice(listingId, retry = false) {
        const id = String(listingId);
        const context = this.results.pricingContext;
        if (
            this.results.pricingMode !== 'page' ||
            !context ||
            this.ui.isLoading
        )
            return;
        if (
            !retry &&
            ['failed', 'expired'].includes(this.results.mapPriceStates[id])
        )
            return;
        if (Object.hasOwn(this.results.listingPrices, id)) return;
        this.results.mapPriceStates = {
            ...this.results.mapPriceStates,
            [id]: 'loading'
        };
        const result = await prototypeMapPrices.get(id);
        if (context !== this.results.pricingContext || result.state === 'stale')
            return;
        this.results.mapPriceStates = {
            ...this.results.mapPriceStates,
            [id]: result.state
        };
        if (result.state === 'ready') {
            this.results.listingPrices = {
                ...this.results.listingPrices,
                [id]: result.price
            };
        }
        this.results.mapPriceVersion++;
    },

    getListingPrice(listingId) {
        return this.results.listingPrices[String(listingId)] ?? null;
    },

    getListingStay(listingId) {
        return this.results.listingStays[String(listingId)] ?? null;
    },

    getListingPriceLabel(listingId) {
        const nights = Number(this.getListingStay(listingId)?.nights || 0);
        if (nights > 1) return `/ ${nights} nights`;
        if (nights === 1) return 'total';
        return this.priceLabel;
    },

    // Helper: Get current page's listings (sliced for pagination)
    getPaginatedListings() {
        // Server-side pagination: The "listings" array IS the paginated slice
        return this.results.listings;
    },

    // Helper: Format price as USD currency
    formatPrice(price) {
        if (!price) return null;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    },

    // Helper: Format dates for display
    // "2026-02-25" → "Feb 25", "2026-01-05 to 2026-01-07" → "Jan 5-7"
    get formattedDates() {
        return this.formatDateRange(this.dates);
    },

    formatDateRange(dates) {
        if (!dates) return dates;

        const parts = dates.split(' to ');
        const startDate = new Date(parts[0].trim() + 'T00:00:00');
        const endDate = parts[1]
            ? new Date(parts[1].trim() + 'T00:00:00')
            : startDate;

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()))
            return dates;

        const currentYear = new Date().getFullYear();
        const startMonth = startDate.toLocaleString('en-US', {
            month: 'short'
        });
        const startDay = startDate.getDate();
        const startYear = startDate.getFullYear();

        // Single date or same date on both sides
        if (startDate.getTime() === endDate.getTime()) {
            return startYear === currentYear
                ? `${startMonth} ${startDay}`
                : `${startMonth} ${startDay}, ${startYear}`;
        }

        const endMonth = endDate.toLocaleString('en-US', { month: 'short' });
        const endDay = endDate.getDate();
        const endYear = endDate.getFullYear();
        const sameMonth =
            startDate.getMonth() === endDate.getMonth() &&
            startYear === endYear;
        const sameYear = startYear === endYear;
        const showYear = startYear !== currentYear || endYear !== currentYear;

        if (sameMonth) {
            return showYear
                ? `${startMonth} ${startDay}-${endDay}, ${startYear}`
                : `${startMonth} ${startDay}-${endDay}`;
        }
        if (sameYear) {
            return showYear
                ? `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`
                : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
        }
        return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
    },

    clearDateSelection() {
        this.dates = '';
        this.flexibleMonth = '';
        this.flexibleNights = 2;
        this.flexibleMaxNights = 3;
        this.ui.validationMessage = null;
    },

    resetGuestSelection() {
        this.adults = 1;
        this.children = 0;
        this.pets = 0;
        this.filters.bedrooms = 1;
        this.updateGuests();
        this.ui.validationMessage = null;
    },

    clearComposerCriteria() {
        this.composerDestinationSlug = null;
        this.where = '';
        this.whereData = null;
        this.mapAreaLabel = null;
        this._visualWhereMatch = null;
        this.clearBounds();
        this.dateMode = 'exact';
        this.clearDateSelection();
        this.resetGuestSelection();
        this.resetFilters();
    },

    get hasDateSelection() {
        return this.dateMode === 'flexible'
            ? Boolean(this.flexibleMonth)
            : Boolean(this.dates);
    },

    get hasGuestSelection() {
        return (
            Number(this.adults) !== 1 ||
            Number(this.children) !== 0 ||
            this.petCount !== 0 ||
            Number(this.filters.bedrooms) !== 1
        );
    },

    formatFlexibleMonth(value, style = 'long') {
        const match = /^(\d{4})-(\d{2})$/.exec(String(value || ''));
        if (!match) return '';
        const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
        const label = date.toLocaleString('en-US', {
            month: style === 'short' ? 'short' : 'long'
        });

        return style === 'short' && label === 'Sep' ? 'Sept' : label;
    },

    normalizeFlexibleStayRange(minimumNights, maximumNights) {
        const minimum = Number(minimumNights);
        const maximum = Number(maximumNights);
        const exactOption = FLEXIBLE_STAY_OPTIONS.find(
            (option) =>
                option.minimumNights === minimum &&
                option.maximumNights === maximum
        );
        if (exactOption) return exactOption;

        const legacyOption = FLEXIBLE_STAY_OPTIONS.find(
            (option) =>
                minimum >= option.minimumNights &&
                minimum <= option.maximumNights
        );
        if (legacyOption) return legacyOption;

        const nextOption = FLEXIBLE_STAY_OPTIONS.find(
            (option) => minimum < option.minimumNights
        );
        if (nextOption) return nextOption;

        return FLEXIBLE_STAY_OPTIONS[0];
    },

    selectFlexibleStayOption(option) {
        const stayRange = this.normalizeFlexibleStayRange(
            option?.minimumNights,
            option?.maximumNights
        );
        this.flexibleNights = stayRange.minimumNights;
        this.flexibleMaxNights = stayRange.maximumNights;
        this.ui.validationMessage = null;
    },

    isFlexibleStayOptionSelected(option) {
        return (
            this.flexibleNights === option.minimumNights &&
            this.flexibleMaxNights === option.maximumNights
        );
    },

    formatFlexibleStayLength(minimumNights, maximumNights) {
        const stayRange = this.normalizeFlexibleStayRange(
            minimumNights,
            maximumNights
        );
        return stayRange.label;
    },

    get flexibleDateLabel() {
        if (!this.flexibleMonth) return 'Choose a month';
        return `${this.formatFlexibleMonth(this.flexibleMonth, 'short')}, ${this.formatFlexibleStayLength(this.flexibleNights, this.flexibleMaxNights)}`;
    },

    get flexibleStayOptions() {
        return FLEXIBLE_STAY_OPTIONS;
    },

    get flexibleMonthOptions() {
        const now = new Date();
        return Array.from({ length: 11 }, (_, index) => {
            const date = new Date(now.getFullYear(), now.getMonth() + index, 1);
            return {
                value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
                label: date.toLocaleString('en-US', { month: 'long' }),
                year: date.getFullYear()
            };
        });
    },

    get splitDates() {
        if (!this.dates) return null;

        const fmt = (date) =>
            `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
        const parts = this.dates.split(' to ');
        const startDate = new Date(parts[0].trim() + 'T00:00:00');
        const endDate = parts[1]
            ? new Date(parts[1].trim() + 'T00:00:00')
            : null;

        if (isNaN(startDate.getTime())) return null;

        const checkIn = fmt(startDate);
        const checkOut =
            endDate && !isNaN(endDate.getTime()) ? fmt(endDate) : null;

        return { checkIn, checkOut };
    },

    // Toggle map mode
    toggleMapMode() {
        window.scrollTo({ top: 0, behavior: 'instant' });
        this.ui.mapMode = !this.ui.mapMode;
    }
});
